// frontend/src/pages/company/ProductVariants.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { productAPI } from '../../services/api';

const ProductVariants = () => {
  const { id } = useParams(); // product_id
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);
  const [formData, setFormData] = useState({
    variant_name: '',
    price: '',
    stock_quantity: 0,
    attributes: [{ key: '', value: '' }]
  });

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productAPI.getById(id);
      const productData = response.data.data;
      
      if (!productData.has_variants) {
        alert('Энэ бараа вариант ашигладаггүй');
        navigate('/company/products');
        return;
      }
      
      setProduct(productData);
      setVariants(productData.variants || []);
    } catch (error) {
      console.error('Fetch product error:', error);
      alert('Бараа татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingVariant(null);
    setFormData({
      variant_name: '',
      price: product?.base_price || '',
      stock_quantity: 0,
      attributes: [{ key: '', value: '' }]
    });
    setShowModal(true);
  };

  const handleEdit = (variant) => {
    setEditingVariant(variant);
    
    // Convert JSONB attributes to array
    const attributesArray = variant.attributes 
      ? Object.entries(variant.attributes).map(([key, value]) => ({ key, value }))
      : [{ key: '', value: '' }];
    
    setFormData({
      variant_name: variant.variant_name,
      price: variant.price,
      stock_quantity: variant.stock_quantity,
      attributes: attributesArray
    });
    setShowModal(true);
  };

  const handleAddAttribute = () => {
    setFormData({
      ...formData,
      attributes: [...formData.attributes, { key: '', value: '' }]
    });
  };

  const handleRemoveAttribute = (index) => {
    const newAttributes = formData.attributes.filter((_, i) => i !== index);
    setFormData({ ...formData, attributes: newAttributes });
  };

  const handleAttributeChange = (index, field, value) => {
    const newAttributes = [...formData.attributes];
    newAttributes[index][field] = value;
    setFormData({ ...formData, attributes: newAttributes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Convert attributes array to object
      const attributesObject = {};
      formData.attributes.forEach(attr => {
        if (attr.key && attr.value) {
          attributesObject[attr.key] = attr.value;
        }
      });

      const submitData = {
        variant_name: formData.variant_name,
        price: parseFloat(formData.price),
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        attributes: attributesObject
      };

      if (editingVariant) {
        await productAPI.updateVariant(id, editingVariant.id, submitData);
        alert('Вариант амжилттай шинэчлэгдлээ');
      } else {
        await productAPI.createVariant(id, submitData);
        alert('Вариант амжилттай үүслээ');
      }
      
      setShowModal(false);
      fetchProduct();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  const handleDelete = async (variantId) => {
    if (!confirm('Энэ вариантыг устгахдаа итгэлтэй байна уу?')) return;
    
    try {
      await productAPI.deleteVariant(id, variantId);
      alert('Вариант амжилттай устлаа');
      fetchProduct();
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  if (loading) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/company/products')}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Барааны варианууд</h1>
              <p className="text-sm text-gray-600 mt-1">{product?.product_name}</p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="btn btn-primary flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Вариант нэмэх</span>
          </button>
        </div>

        {/* PRODUCT INFO CARD */}
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 border border-primary-200">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center">
              <span className="text-3xl">📦</span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-2">{product?.product_name}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">SKU:</span>
                  <span className="ml-2 font-semibold text-gray-900">{product?.sku}</span>
                </div>
                <div>
                  <span className="text-gray-600">Үндсэн үнэ:</span>
                  <span className="ml-2 font-semibold text-gray-900">
                    {product?.base_price?.toLocaleString()}₮
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Ангилал:</span>
                  <span className="ml-2 font-semibold text-gray-900">{product?.category?.name || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Нийт вариант:</span>
                  <span className="ml-2 font-semibold text-primary-600">{variants.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VARIANTS LIST */}
        {variants.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-300">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Вариант байхгүй байна</h3>
            <p className="text-gray-600 mb-6">Эхний вариантаа нэмээд эхлээрэй</p>
            <button onClick={handleCreate} className="btn btn-primary">+ Вариант нэмэх</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {variants.map((variant) => (
              <div
                key={variant.id}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 text-lg">{variant.variant_name}</h3>
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                    SKU: {variant.sku}
                  </span>
                </div>

                {/* Attributes */}
                {variant.attributes && Object.keys(variant.attributes).length > 0 && (
                  <div className="mb-4 space-y-2">
                    {Object.entries(variant.attributes).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded font-medium">
                          {key}
                        </span>
                        <span className="text-gray-600">→</span>
                        <span className="text-gray-900 font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Price & Stock */}
                <div className="flex items-center justify-between mb-4 pt-3 border-t border-gray-100">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Үнэ</div>
                    <div className="text-lg font-bold text-primary-600">
                      {variant.price?.toLocaleString()}₮
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-600 mb-1">Үлдэгдэл</div>
                    <div className="text-lg font-bold text-green-600">
                      {variant.stock_quantity}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(variant)}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium text-sm"
                  >
                    ✏️ Засах
                  </button>
                  <button
                    onClick={() => handleDelete(variant.id)}
                    className="flex-1 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition font-medium text-sm"
                  >
                    🗑️ Устгах
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-3xl">📦</span>
              <span>{editingVariant ? 'Вариант засах' : 'Вариант нэмэх'}</span>
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Variant Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Вариантын нэр *
                </label>
                <input
                  type="text"
                  value={formData.variant_name}
                  onChange={(e) => setFormData({ ...formData, variant_name: e.target.value })}
                  className="admin-input"
                  required
                  placeholder="Жнь: Хар - 256GB"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Вариантыг тодорхойлох нэр (хэрэглэгч харна)
                </p>
              </div>

              {/* Attributes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attributes * (Шинж чанарууд)
                </label>
                <div className="space-y-3">
                  {formData.attributes.map((attr, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={attr.key}
                        onChange={(e) => handleAttributeChange(index, 'key', e.target.value)}
                        className="admin-input flex-1"
                        placeholder="Жнь: Өнгө"
                      />
                      <input
                        type="text"
                        value={attr.value}
                        onChange={(e) => handleAttributeChange(index, 'value', e.target.value)}
                        className="admin-input flex-1"
                        placeholder="Жнь: Хар"
                      />
                      {formData.attributes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveAttribute(index)}
                          className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleAddAttribute}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  + Attribute нэмэх
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  Өнгө, хэмжээ, багтаамж гэх мэт шинж чанарууд
                </p>
              </div>

              {/* Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Үнэ *
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="admin-input"
                    required
                    step="0.01"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Үлдэгдэл
                  </label>
                  <input
                    type="number"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    className="admin-input"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  className="flex-1 btn btn-primary"
                >
                  ✅ Хадгалах
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default ProductVariants;